import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Timer,
  CheckCircle,
} from "lucide-react";
import { Button, Text } from "@core/index";
import { VisualMatchingResponseDto } from "@packages/api/madlenAPI.schemas";

interface VisualMatchingSmartboardViewProps {
  isOpen: boolean;
  onClose: () => void;
  visualMatchingData: VisualMatchingResponseDto;
}

interface MatchingPair {
  imageUrl: string;
  name: string;
  id: string;
}

interface Selection {
  type: "image" | "text";
  id: string;
  pairId: string;
}

// Preload sounds to avoid delay on first play
const preloadedCorrectAudio = new Audio("/sounds/correct.mp3");
const preloadedWrongAudio = new Audio("/sounds/wrong.mp3");
preloadedCorrectAudio.volume = 0.5;
preloadedWrongAudio.volume = 0.5;
preloadedCorrectAudio.load();
preloadedWrongAudio.load();

// Sound utility function - creates a new Audio instance but benefits from browser cache due to preload
const playSound = (soundType: "correct" | "wrong") => {
  try {
    const src =
      soundType === "correct"
        ? preloadedCorrectAudio.src
        : preloadedWrongAudio.src;
    const audio = new Audio(src);
    audio.volume = 0.5;
    audio
      .play()
      .catch((error) => console.warn("Sound playback failed:", error));
    audio.addEventListener("ended", () => audio.remove());
  } catch (error) {
    console.warn("Sound playback error:", error);
  }
};

const VisualMatchingSmartboardView: React.FC<
  VisualMatchingSmartboardViewProps
> = ({ isOpen, onClose, visualMatchingData }) => {
  const { t } = useTranslation();

  // Game state
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedItem, setSelectedItem] = useState<Selection | null>(null);
  const [matchedPairs, setMatchedPairs] = useState<Set<string>>(new Set());
  const [isComplete, setIsComplete] = useState(false);

  // Timer state
  const [questionStartTime, setQuestionStartTime] = useState<number>(
    Date.now(),
  );
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [isTimerPaused, setIsTimerPaused] = useState(false);

  // Wrong selection state
  const [wrongSelectionIds, setWrongSelectionIds] = useState<string[]>([]);

  // Get current question data
  const currentQuestion = visualMatchingData.questions?.[currentQuestionIndex];
  const totalQuestions = visualMatchingData.questions?.length || 0;

  // Convert question options to matching pairs
  // Modes: 'categorized' uses current question groups (default), 'mixed' aggregates all questions into a single pool,
  // 'memory' will prepare cards (handled separately) but we still keep pairs for matching logic.
  type Mode = "categorized" | "mixed" | "memory";
  const [mode, setMode] = useState<Mode>("categorized");

  const getPairsForMode = (): MatchingPair[] => {
    const buildAll = (): MatchingPair[] => {
      const allOptions =
        visualMatchingData.questions?.flatMap((q) => q.options || []) || [];
      return allOptions.map((option, index) => ({
        imageUrl: option.imageUrl || "",
        name: option.name || "",
        id: `pair-all-${index}`,
      }));
    };

    if (mode === "mixed" || mode === "memory") {
      return buildAll();
    }

    // categorized defaults to current question options
    return (
      currentQuestion?.options?.map((option, index) => ({
        imageUrl: option.imageUrl || "",
        name: option.name || "",
        id: `pair-${currentQuestionIndex}-${index}`,
      })) || []
    );
  };

  const matchingPairs: MatchingPair[] = getPairsForMode();

  // Memory card types
  interface MemoryCard {
    id: string;
    type: "image" | "text";
    pairId: string;
    imageUrl?: string;
    name?: string;
    isFlipped?: boolean;
    isMatched?: boolean;
    // Visual hint flags
    isWrongHighlight?: boolean;
    isCorrectHighlight?: boolean;
  }

  // Memory mode state
  const [memoryCards, setMemoryCards] = useState<MemoryCard[]>([]);
  const firstFlippedRef = useRef<string | null>(null);
  const secondFlippedRef = useRef<string | null>(null);
  const flipTimeoutRef = useRef<number | null>(null);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const [memoryGridColumns, setMemoryGridColumns] = useState<number>(1);
  const [memoryCardSizePx, setMemoryCardSizePx] = useState<number>(120);

  // Initialize memory cards when mode or question changes
  useEffect(() => {
    if (mode !== "memory") return;

    const pairs = getPairsForMode();
    const cards: MemoryCard[] = [];
    pairs.forEach((p) => {
      cards.push({
        id: `card-image-${p.id}`,
        type: "image",
        pairId: p.id,
        imageUrl: p.imageUrl,
        isFlipped: false,
        isMatched: false,
      });
      cards.push({
        id: `card-text-${p.id}`,
        type: "text",
        pairId: p.id,
        name: p.name,
        isFlipped: false,
        isMatched: false,
      });
    });

    // Shuffle
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }

    setMemoryCards(cards);
    firstFlippedRef.current = null;
    secondFlippedRef.current = null;
    if (flipTimeoutRef.current) {
      window.clearTimeout(flipTimeoutRef.current);
      flipTimeoutRef.current = null;
    }
  }, [mode, visualMatchingData]);

  // Compute best grid to fit all cards into the visible area
  const computeBestGrid = useCallback(() => {
    if (mode !== "memory") return;
    const container = gridContainerRef.current;
    if (!container) return;
    const total = memoryCards.length || 0;
    if (total === 0) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    // Gap between cells in px
    const gap = 12;

    // Try different column counts to maximize card size while fitting all
    let bestColumns = 1;
    let bestSize = 0;

    for (let cols = 1; cols <= total; cols++) {
      const rows = Math.ceil(total / cols);
      const sizeByWidth = (containerWidth - (cols - 1) * gap) / cols;
      const sizeByHeight = (containerHeight - (rows - 1) * gap) / rows;
      const cardSize = Math.floor(Math.min(sizeByWidth, sizeByHeight));
      if (cardSize >= bestSize) {
        bestSize = cardSize;
        bestColumns = cols;
      }
    }

    // Minimum size guard
    const clampedSize = Math.max(bestSize, 64);
    setMemoryGridColumns(bestColumns);
    setMemoryCardSizePx(clampedSize);
  }, [mode, memoryCards.length]);

  useEffect(() => {
    if (mode !== "memory") return;
    computeBestGrid();
    const onResize = () => computeBestGrid();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [mode, computeBestGrid]);

  useEffect(() => {
    if (mode !== "memory") return;
    // Recompute when memory cards change (e.g., initial setup)
    computeBestGrid();
  }, [mode, memoryCards, computeBestGrid]);

  // Shuffle arrays for random positioning
  const [shuffledImages, setShuffledImages] = useState<MatchingPair[]>([]);
  const [shuffledTexts, setShuffledTexts] = useState<MatchingPair[]>([]);

  // Timer effect
  useEffect(() => {
    if (isTimerPaused) return;

    const interval = setInterval(() => {
      setElapsedTime(Date.now() - questionStartTime);
    }, 100);

    return () => clearInterval(interval);
  }, [questionStartTime, isTimerPaused]);

  // Initialize question data when changing questions
  useEffect(() => {
    if (matchingPairs.length > 0) {
      const shuffleArray = <T,>(array: T[]): T[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
      };

      setShuffledImages(shuffleArray(matchingPairs));
      setShuffledTexts(shuffleArray(matchingPairs));
      setMatchedPairs(new Set());
      setSelectedItem(null);
      setQuestionStartTime(Date.now());
      setElapsedTime(0);
      setIsComplete(false);
    }
  }, [currentQuestionIndex, matchingPairs.length]);

  // Handle item selection
  const handleItemClick = useCallback(
    (type: "image" | "text", id: string, pairId: string) => {
      if (mode === "memory") return; // Memory mode uses its own handler
      if (matchedPairs.has(pairId)) return;
      const newSelection: Selection = { type, id, pairId };
      if (!selectedItem) {
        setSelectedItem(newSelection);
        setWrongSelectionIds([]); // Clear wrong selections on new selection
        return;
      }
      if (selectedItem.id === id) {
        setSelectedItem(null);
        setWrongSelectionIds([]); // Clear wrong selections on deselect
        return;
      }
      if (selectedItem.type === type) {
        setSelectedItem(newSelection);
        setWrongSelectionIds([]); // Clear wrong selections when same type is selected
        return;
      }
      if (selectedItem.pairId === pairId) {
        // Correct match
        const newMatchedPairs = new Set(matchedPairs);
        newMatchedPairs.add(pairId);
        setMatchedPairs(newMatchedPairs);
        setSelectedItem(null);
        setWrongSelectionIds([]); // Clear wrong selections on correct match

        // Play correct sound
        playSound("correct");

        if (newMatchedPairs.size === matchingPairs.length) {
          setIsComplete(true);
        }
      } else {
        // Wrong match: highlight both selected and just clicked
        setWrongSelectionIds([selectedItem.id, id]);
        setSelectedItem(null);

        // Play wrong sound
        playSound("wrong");

        // Don't auto-clear wrong selections - they stay until next selection
      }
    },
    [selectedItem, matchedPairs, matchingPairs.length, mode],
  );

  // Memory card click handler
  const handleMemoryCardClick = (cardId: string) => {
    setMemoryCards((prev) => {
      let next = prev;

      // If two wrong cards are open and waiting, close them immediately before processing the new click
      if (firstFlippedRef.current && secondFlippedRef.current) {
        const firstOpenId = firstFlippedRef.current;
        const secondOpenId = secondFlippedRef.current;
        const firstCard = next.find((c) => c.id === firstOpenId);
        const secondCard = next.find((c) => c.id === secondOpenId);
        if (
          firstCard &&
          secondCard &&
          !firstCard.isMatched &&
          !secondCard.isMatched
        ) {
          // Cancel pending auto-close timeout
          if (flipTimeoutRef.current) {
            window.clearTimeout(flipTimeoutRef.current);
            flipTimeoutRef.current = null;
          }
          // Flip both back immediately and clear highlights
          next = next.map((c) =>
            c.id === firstOpenId || c.id === secondOpenId
              ? { ...c, isFlipped: false, isWrongHighlight: false }
              : c,
          );
          firstFlippedRef.current = null;
          secondFlippedRef.current = null;
        }
      }

      const card = next.find((c) => c.id === cardId);
      if (!card || card.isFlipped || card.isMatched) return next;

      // Flip the selected card immediately
      let updated = next.map((c) =>
        c.id === cardId ? { ...c, isFlipped: true } : c,
      );

      // First selection
      if (!firstFlippedRef.current) {
        firstFlippedRef.current = cardId;
        return updated;
      }

      // Second selection
      if (!secondFlippedRef.current && firstFlippedRef.current !== cardId) {
        secondFlippedRef.current = cardId;
        const first = updated.find((c) => c.id === firstFlippedRef.current);
        const second = updated.find((c) => c.id === cardId);
        if (first && second) {
          // If they match
          if (first.pairId === second.pairId && first.type !== second.type) {
            updated = updated.map((c) =>
              c.pairId === first.pairId
                ? {
                    ...c,
                    isMatched: true,
                    isCorrectHighlight: true,
                    isWrongHighlight: false,
                  }
                : c,
            );
            playSound("correct");
            firstFlippedRef.current = null;
            secondFlippedRef.current = null;
            return updated;
          }

          // Wrong match: highlight red, schedule auto-flip unless user clicks a new card
          updated = updated.map((c) =>
            c.id === first.id || c.id === second.id
              ? { ...c, isWrongHighlight: true }
              : c,
          );
          playSound("wrong");
          const scheduledFirstId = first.id;
          const scheduledSecondId = second.id;
          flipTimeoutRef.current = window.setTimeout(() => {
            setMemoryCards((curr) =>
              curr.map((c) =>
                c.id === scheduledFirstId || c.id === scheduledSecondId
                  ? { ...c, isFlipped: false, isWrongHighlight: false }
                  : c,
              ),
            );
            firstFlippedRef.current = null;
            secondFlippedRef.current = null;
            flipTimeoutRef.current = null;
          }, 3000);

          return updated;
        }
      }

      return updated;
    });
  };

  // Navigation handlers
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const toggleTimer = () => {
    setIsTimerPaused(!isTimerPaused);
  };

  // Format time display
  const formatTime = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Get item styling based on state
  const getItemStyling = (id: string, pairId: string, isImage: boolean) => {
    const isMatched = matchedPairs.has(pairId);
    const isSelected = selectedItem?.id === id;
    const isWrongSelected = wrongSelectionIds.includes(id);

    if (isImage) {
      // Image styling
      let baseClass =
        "relative transition-all duration-300 cursor-pointer border-2 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm hover:shadow-md ";

      if (isMatched) {
        baseClass +=
          "border-green-500 ring-2 ring-green-500 ring-opacity-50 pointer-events-none ";
      } else if (isWrongSelected) {
        baseClass +=
          "border-red-500 ring-2 ring-red-500 ring-opacity-50 animate-pulse ";
      } else if (isSelected) {
        baseClass +=
          "border-blue-500 ring-2 ring-blue-500 ring-opacity-50 transform scale-105 shadow-lg ";
      } else {
        baseClass +=
          "border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400 hover:ring-2 hover:ring-gray-300 dark:hover:ring-gray-500/60 ";
      }

      return baseClass;
    } else {
      // Text styling
      let baseClass =
        "relative transition-all duration-300 cursor-pointer border-2 rounded-xl p-4 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md ";

      if (isMatched) {
        baseClass +=
          "border-green-500 bg-green-50 dark:bg-green-900/20 ring-2 ring-green-500 ring-opacity-50 pointer-events-none ";
      } else if (isWrongSelected) {
        baseClass +=
          "border-red-500 bg-red-50 dark:bg-red-900/20 ring-2 ring-red-500 ring-opacity-50 animate-pulse ";
      } else if (isSelected) {
        baseClass +=
          "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 ring-opacity-50 shadow-lg "; // removed scale-105
      } else {
        baseClass +=
          "border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/60 ";
      }

      return baseClass;
    }
  };

  // Get image overlay styling for opacity filters
  const getImageOverlayStyling = (id: string, pairId: string) => {
    const isMatched = matchedPairs.has(pairId);
    const isSelected = selectedItem?.id === id;
    const isWrongSelected = wrongSelectionIds.includes(id);

    if (isMatched) {
      return "absolute inset-0 bg-green-500 bg-opacity-30 pointer-events-none transition-all duration-300";
    } else if (isWrongSelected) {
      return "absolute inset-0 bg-red-500 bg-opacity-30 pointer-events-none transition-all duration-300";
    } else if (isSelected) {
      return "absolute inset-0 bg-blue-500 bg-opacity-20 pointer-events-none transition-all duration-300";
    } else {
      return "absolute inset-0 bg-transparent pointer-events-none transition-all duration-300";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full h-full bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 border-b border-gray-200 dark:border-gray-600 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-4">
            {/* Left Section - Close Button and Title */}
            <div className="flex items-center gap-3 justify-self-start w-full">
              <Button
                onClick={onClose}
                size="sm"
                variant="outline"
                rounded="full"
                className="h-9 w-9 p-0 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600 shadow-sm"
              >
                <X className="w-4 h-4" />
              </Button>

              <div className="flex-1 min-w-0">
                <Text
                  variant="h3"
                  className="text-lg font-bold text-gray-900 dark:text-white truncate"
                >
                  {currentQuestion?.topic ||
                    t("visualMatching.smartboard.questionTitle", {
                      number: currentQuestionIndex + 1,
                    })}
                </Text>
              </div>
            </div>

            {/* Mode selector - centered, tab styled */}
            <div className="justify-self-center">
              <div
                role="tablist"
                aria-label="mode-tabs"
                className="flex items-center gap-1 rounded-full bg-white/70 dark:bg-dark-300 backdrop-blur px-1 py-1 shadow-sm"
              >
                <Button
                  role="tab"
                  aria-selected={mode === "categorized"}
                  onClick={() => setMode("categorized")}
                  size="sm"
                  variant="ghost"
                  rounded="full"
                  className={`text-sm px-4 py-1.5 transition-colors hover:bg-dark-700 dark:hover:bg-dark-500 ${
                    mode === "categorized"
                      ? "bg-dark-900 text-white shadow"
                      : "text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {t(
                    "visualMatching.smartboard.mode.categorized",
                    "Categorized",
                  )}
                </Button>
                <Button
                  role="tab"
                  aria-selected={mode === "mixed"}
                  onClick={() => setMode("mixed")}
                  size="sm"
                  variant="ghost"
                  rounded="full"
                  className={`text-sm px-4 py-1.5 transition-colors hover:bg-dark-700 dark:hover:bg-dark-500 ${
                    mode === "mixed"
                      ? "bg-dark-900 text-white shadow"
                      : "text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {t("visualMatching.smartboard.mode.mixed", "Mixed")}
                </Button>
                <Button
                  role="tab"
                  aria-selected={mode === "memory"}
                  onClick={() => setMode("memory")}
                  size="sm"
                  variant="ghost"
                  rounded="full"
                  className={`text-sm px-4 py-1.5 transition-colors hover:bg-dark-700 dark:hover:bg-dark-500 ${
                    mode === "memory"
                      ? "bg-dark-900 text-white shadow"
                      : "text-gray-700 dark:text-gray-200"
                  }`}
                >
                  {t("visualMatching.smartboard.mode.memory", "Memory")}
                </Button>
              </div>
            </div>

            {/* Right Section - Timer and Controls */}
            <div className="flex items-center gap-3 justify-self-end">
              {/* Timer */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={toggleTimer}
                  size="sm"
                  rounded="full"
                  className="h-8 w-8 p-0"
                  aria-pressed={!isTimerPaused}
                >
                  {isTimerPaused ? (
                    <Play className="w-4 h-4" />
                  ) : (
                    <Pause className="w-4 h-4" />
                  )}
                </Button>
                <Text
                  variant="p"
                  className="text-base font-mono font-semibold tracking-wider text-gray-900 dark:text-white"
                >
                  {formatTime(elapsedTime)}
                </Text>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col lg:flex-row gap-6 p-6 overflow-hidden">
          {mode === "memory" ? (
            // Memory Card Grid (single pane) - fit all cards, no scroll
            <div ref={gridContainerRef} className="w-full h-full p-3">
              <div
                className="grid"
                style={{
                  gridTemplateColumns: `repeat(${memoryGridColumns}, ${memoryCardSizePx}px)`,
                  gap: 12,
                  justifyContent: "center",
                  alignContent: "center",
                }}
              >
                {memoryCards.map((card) => {
                  const borderClass =
                    card.isMatched || card.isCorrectHighlight
                      ? "border-green-500"
                      : card.isWrongHighlight
                        ? "border-red-500 animate-pulse"
                        : "border-gray-200 dark:border-gray-600";

                  const bgClass = card.isMatched
                    ? "bg-green-50 dark:bg-green-900/20"
                    : "bg-white dark:bg-gray-800";

                  return (
                    <div
                      key={card.id}
                      style={{
                        width: memoryCardSizePx,
                        height: memoryCardSizePx,
                        perspective: 1000,
                      }}
                    >
                      <div
                        onClick={() => handleMemoryCardClick(card.id)}
                        className="relative w-full h-full"
                      >
                        <div
                          className={`absolute inset-0 rounded-lg overflow-hidden border-2 shadow-sm ${borderClass} ${bgClass}`}
                          style={{
                            transform:
                              card.isFlipped || card.isMatched
                                ? "rotateY(180deg)"
                                : "rotateY(0deg)",
                            transformStyle: "preserve-3d",
                            WebkitTransformStyle: "preserve-3d" as any,
                            transition: "transform 700ms",
                            willChange: "transform",
                          }}
                        >
                          {/* Front (face-down) */}
                          <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{
                              transformOrigin: "center",
                              transformStyle: "preserve-3d",
                              WebkitTransformStyle: "preserve-3d" as any,
                              // slight z separation to avoid z-fighting
                              willChange: "opacity, transform",
                              transformBox: "fill-box" as any,
                              // keep on front plane
                              // translateZ small values are safe across browsers
                              // but not all support it with CSSOM types
                              // so we keep it minimal and optional
                              // @ts-ignore
                              transform: `rotateY(0deg) translateZ(0.01px)`,
                              opacity: card.isFlipped || card.isMatched ? 0 : 1,
                              transition: "opacity 200ms",
                              zIndex: card.isFlipped || card.isMatched ? 0 : 2,
                              pointerEvents:
                                card.isFlipped || card.isMatched
                                  ? "none"
                                  : "auto",
                            }}
                          >
                            <div className="absolute inset-0 bg-gray-200 dark:bg-dark-600" />
                            <div className="relative text-sm font-medium text-gray-700 dark:text-gray-200">
                              {t(
                                "visualMatching.smartboard.tapToFlip",
                                "Click to flip",
                              )}
                            </div>
                          </div>

                          {/* Back (face-up) */}
                          <div
                            className="absolute inset-0 flex items-center justify-center"
                            style={{
                              transformOrigin: "center",
                              transformStyle: "preserve-3d",
                              WebkitTransformStyle: "preserve-3d" as any,
                              // @ts-ignore
                              transform: `rotateY(180deg) translateZ(0.01px)`,
                              opacity: card.isFlipped || card.isMatched ? 1 : 0,
                              transition: "opacity 200ms",
                              zIndex: card.isFlipped || card.isMatched ? 2 : 0,
                              pointerEvents:
                                card.isFlipped || card.isMatched
                                  ? "auto"
                                  : "none",
                            }}
                          >
                            <div
                              className="absolute inset-0 bg-white dark:bg-gray-800"
                              style={{ zIndex: 0 }}
                            />
                            <div
                              className="relative w-full h-full flex items-center justify-center"
                              style={{ zIndex: 1 }}
                            >
                              {card.type === "image" ? (
                                card.imageUrl ? (
                                  <img
                                    src={card.imageUrl}
                                    alt={card.name}
                                    className="absolute inset-0 w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="text-xs text-gray-600 dark:text-gray-300 text-center px-2">
                                    {t("visualMatching.smartboard.noImage")}
                                  </div>
                                )
                              ) : (
                                <Text
                                  variant="p"
                                  className="text-base font-semibold text-center text-xl text-gray-900 dark:text-gray-100 px-2"
                                >
                                  {card.name}
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            // Categorized / Mixed: two-pane layout (images + words)
            <>
              {/* Images Section */}
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex items-center justify-between mb-4">
                  <Text
                    variant="h4"
                    className="text-lg font-bold text-gray-800 dark:text-gray-200"
                  >
                    {t("visualMatching.smartboard.imagesTitle")}
                  </Text>
                  <div className="sm:hidden">
                    <Text
                      variant="p"
                      className="text-sm text-gray-600 dark:text-gray-400"
                    >
                      {matchedPairs.size} / {matchingPairs.length}
                    </Text>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 p-2">
                    {shuffledImages.map((pair) => (
                      <div
                        key={`image-${pair.id}`}
                        className={
                          getItemStyling(`image-${pair.id}`, pair.id, true) +
                          " aspect-square"
                        }
                        onClick={() =>
                          handleItemClick("image", `image-${pair.id}`, pair.id)
                        }
                      >
                        {pair.imageUrl ? (
                          <img
                            src={pair.imageUrl}
                            alt={pair.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = "none";
                              const placeholder =
                                target.nextElementSibling as HTMLElement;
                              if (placeholder)
                                placeholder.style.display = "flex";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <Text
                              variant="p"
                              className="text-gray-500 dark:text-gray-400 text-xs text-center px-2"
                            >
                              {t("visualMatching.smartboard.noImage")}
                            </Text>
                          </div>
                        )}
                        <div
                          className={getImageOverlayStyling(
                            `image-${pair.id}`,
                            pair.id,
                          )}
                        />
                        {matchedPairs.has(pair.id) && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                            <CheckCircle className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Words Section */}
              <div className="flex-1 flex flex-col min-h-0">
                <Text
                  variant="h4"
                  className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-4"
                >
                  {t("visualMatching.smartboard.wordsTitle")}
                </Text>
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 p-2">
                    {shuffledTexts.map((pair) => (
                      <div
                        key={`text-${pair.id}`}
                        className={getItemStyling(
                          `text-${pair.id}`,
                          pair.id,
                          false,
                        )}
                        onClick={() =>
                          handleItemClick("text", `text-${pair.id}`, pair.id)
                        }
                      >
                        <Text
                          variant="p"
                          className="text-base font-semibold text-center text-gray-900 dark:text-white"
                        >
                          {pair.name}
                        </Text>
                        {matchedPairs.has(pair.id) && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1">
                            <CheckCircle className="w-3 h-3" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom Controls - only for categorized mode */}
        {mode === "categorized" && (
          <div className="flex-shrink-0 bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 border-t border-gray-200 dark:border-gray-600 p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Left Controls */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                  size="sm"
                  variant="outline"
                  rounded="lg"
                  className="bg-white dark:bg-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span className="ml-1 text-sm hidden sm:inline">
                    {t("visualMatching.smartboard.previous")}
                  </span>
                </Button>

                <Button
                  onClick={handleNextQuestion}
                  disabled={currentQuestionIndex >= totalQuestions - 1}
                  size="sm"
                  variant="outline"
                  rounded="lg"
                  className="bg-white dark:bg-gray-700"
                >
                  <span className="mr-1 text-sm hidden sm:inline">
                    {t("visualMatching.smartboard.next")}
                  </span>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              {/* Center Progress - Prominent Display */}
              <div className="flex items-center justify-center">
                {isComplete ? (
                  <div className="">
                    <div className="flex items-center gap-3">
                      <Text
                        variant="p"
                        className="text-lg font-bold text-green-700 dark:text-green-400"
                      >
                        {t("visualMatching.smartboard.completed")}
                      </Text>
                    </div>
                  </div>
                ) : (
                  <div className="">
                    <div className="flex items-center gap-3">
                      <Text
                        variant="p"
                        className="text-xl font-bold text-gray-900 dark:text-white"
                      >
                        {currentQuestionIndex + 1} / {totalQuestions}
                      </Text>
                    </div>
                  </div>
                )}
              </div>

              {/* Right Instructions */}
              <div className="text-center sm:text-right">
                <Text
                  variant="p"
                  className="text-sm text-gray-600 dark:text-gray-400"
                >
                  {selectedItem
                    ? t("visualMatching.smartboard.selectMatch")
                    : t("visualMatching.smartboard.selectFirst")}
                </Text>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisualMatchingSmartboardView;
